import { useState } from 'react';
import axios from 'axios';
import {
    TableRow,
    TableCell,
    IconButton,
    LinearProgress,
    Typography,
    Switch,
    Tooltip,
    Stack
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { formatarCNPJ, hasPermission } from '../../../../configs/functions';
import ConfirmDialog from '../../../common/ConfirmDialog';
import { useCommonItems } from '../../../../contexts/CommonItensProvider';
import { EditIcon, TrashIcon } from '../../../common/Icons';
import { useModuleContext } from '../../../../contexts/ModuleProvider';

const COLUMNS = 8;
const CONNECTOR_LEFT = 28;

function CompanyUnitRow({ filial, actionsAllowed, handleEmpresa, handleOpenModalFilial, setDeletingFilial }) {
    const active = filial.status === 'ativa';
    const textColor = active ? 'text.primary' : 'text.secondary';

    return (
        <TableRow
            hover
        >
            <TableCell />
            <TableCell />
            <TableCell sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                {formatarCNPJ(filial.cnpj)}
            </TableCell>
            <TableCell sx={{ color: textColor }}>{filial.nomeFantasia}</TableCell>
            <TableCell sx={{ color: textColor }}>{`${filial.cidade} - ${filial.estado}`}</TableCell>
            <TableCell>
                <Switch
                    color="success"
                    checked={active}
                    onChange={() => handleEmpresa(filial)}
                    disabled={!hasPermission(["admin", "admin_empresas", "update_empresas"]) || !actionsAllowed}
                />
            </TableCell>
            <TableCell />
            <TableCell align="right">
                <Stack direction="row" justifyContent="flex-end">
                    {hasPermission(["admin", "admin_empresas", "update_empresas"]) &&
                        <Tooltip title={actionsAllowed ? "Editar Filial" : "Visualizar Filial"}>
                            <IconButton
                                aria-label="Editar"
                                onClick={() => handleOpenModalFilial(filial)}
                            >
                                {actionsAllowed ? <EditIcon /> : <Visibility color='primary' />}
                            </IconButton>
                        </Tooltip>
                    }

                    {hasPermission(["admin", "admin_empresas", "delete_empresas"]) && actionsAllowed &&
                        <Tooltip title="Excluir Filial">
                            <span>
                                <IconButton
                                    aria-label="Excluir"
                                    onClick={() => setDeletingFilial({ id: filial.id, nomeFantasia: filial.nomeFantasia })}
                                    disabled={!active}
                                >
                                    <TrashIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    }
                </Stack>
            </TableCell>
        </TableRow>
    );
}

function CompanyUnits({ filiais, loaded, handleCarregaFiliais, handleOpenModalFilial }) {
    const { exibirAlerta } = useCommonItems();
    const { verifyIntegrationMode } = useModuleContext();

    const [deletingFilial, setDeletingFilial] = useState(null);

    const deleteEmpresa = (id) => {
        axios.delete(`empresas/${id}`)
            .then((response) => {
                const message = response.data.message ? response.data.message : 'Empresa excluída com sucesso'
                exibirAlerta('Empresa excluída', message, 'success')
                handleCarregaFiliais();
            }).catch((error) => {
                console.log(error);
                exibirAlerta('Erro ao alterar status', '', 'error')
            })

        setDeletingFilial(null);
    }

    const handleEmpresa = (filial) => {
        if (!hasPermission(["admin", "admin_empresas", "update_empresas"])) {
            exibirAlerta('Erro', 'Você não tem permissão para executar esta ação.', 'error')
            return false;
        }

        const novoStatus = (filial.status === 'ativa') ? 'inativa' : 'ativa'

        axios.patch(`empresas/${filial.id}`, { status: novoStatus })
            .then((response) => {
                const message = filial.status === 'ativa' ? 'Filial inativada com sucesso' : 'Filial ativada com sucesso'
                exibirAlerta('Sucesso', message, 'success')
                handleCarregaFiliais();
            }).catch((error) => {
                console.log(error)
                exibirAlerta('Erro', 'Não foi possível executar a ação.', 'error')
            })
    }

    if (!loaded) {
        return (
            <TableRow sx={{ backgroundColor: 'grey.50' }}>
                <TableCell colSpan={COLUMNS} sx={{ py: 2 }}>
                    <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />
                    <Typography variant="body2" color="text.secondary">Verificando filiais...</Typography>
                </TableCell>
            </TableRow>
        );
    }

    if (!filiais) return null;

    return (
        <>
            {filiais.map((filial, index) => (
                <CompanyUnitRow
                    key={filial.id}
                    filial={filial}
                    isLast={index === filiais.length - 1}
                    handleEmpresa={handleEmpresa}
                    handleOpenModalFilial={handleOpenModalFilial}
                    setDeletingFilial={setDeletingFilial}
                    actionsAllowed={verifyIntegrationMode("Empresa", filial.id)}
                />
            ))}

            {deletingFilial &&
                <ConfirmDialog
                    description={`Tem certeza que deseja excluir a filial "${deletingFilial.nomeFantasia}"?`}
                    title={'Excluir Empresa'}
                    goAction={() => deleteEmpresa(deletingFilial.id)}
                    handleClose={() => setDeletingFilial(null)}
                    state={deletingFilial}
                />
            }
        </>
    );
}

export default CompanyUnits;